import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import { Button, ModalContentContainer, TextField } from '@/components';
import { useForm } from '@/hooks';
import { selectUserProfileResult, useUpdatePasswordMutation } from '@/store';
import { SnackbarUtils } from '@/utils';

type EditPasswordFormState = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export const EditPassword = ({
  onSuccess,
  onCancel,
}: {
  onSuccess?: () => void;
  onCancel?: () => void;
}) => {
  const [updatePassword] = useUpdatePasswordMutation();
  const { form, errors, setErrors, onChange, validateForm } =
    useForm<EditPasswordFormState>({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });

  const { data: profile } = useSelector(selectUserProfileResult);

  const onSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    if (!profile?.email) {
      setErrors(errors => ({
        ...errors,
        oldPassword:
          'Something went wrong. Please try again or contact support.',
      }));
      return;
    }

    setErrors({
      oldPassword: undefined,
      newPassword: undefined,
      confirmPassword: undefined,
    });

    try {
      const res = await updatePassword({
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
        email: profile.email,
      }).unwrap();
      if (res) {
        SnackbarUtils.success('Password updated successfully.');
        onSuccess?.();
      } else {
        setErrors(errors => ({
          ...errors,
          oldPassword: 'Old password is incorrect',
        }));
      }
    } catch {
      setErrors(errors => ({
        ...errors,
        oldPassword:
          'Something went wrong. Please try again or contact support.',
      }));
    }
  };

  return (
    <ModalContentContainer>
      <Typography variant="h1">Change Password</Typography>
      <TextField
        id="oldPassword"
        fullWidth
        label="Old Password"
        type="password"
        placeholder="••••••••"
        sx={{ mt: 6 }}
        value={form.oldPassword}
        error={!!errors.oldPassword}
        helperText={errors.oldPassword}
        onChange={onChange}
      />
      <TextField
        id="newPassword"
        fullWidth
        label="New Password"
        type="password"
        placeholder="••••••••"
        sx={{ mt: 2 }}
        value={form.newPassword}
        error={!!errors.newPassword}
        helperText={errors.newPassword}
        onChange={onChange}
      />
      <TextField
        id="confirmPassword"
        fullWidth
        label="Confirm Password"
        type="password"
        placeholder="••••••••"
        sx={{ mt: 2 }}
        value={form.confirmPassword}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        onChange={onChange}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          mt: 4,
        }}>
        <Button
          variant="secondary"
          label="Cancel"
          fullWidth={false}
          skinny={false}
          sx={{ width: '50%' }}
          onClick={() => {
            onCancel?.();
          }}
        />
        <Button
          variant="primary"
          label="Save"
          fullWidth={false}
          skinny={false}
          sx={{ width: '50%' }}
          onClick={onSubmit}
        />
      </Box>
    </ModalContentContainer>
  );
};
